import React, { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/Store';
import { BlogPost } from '../../models/blog/BlogPost';
import BlogTags from '../../components/blog/BlogTags';
import TerraformIcon from '../../components/blog/TerraformIcon';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useLocale } from '../../context/LocaleContext';
import { usePageMeta } from '../../hooks/usePageMeta';
import './BlogListPage.css';

const PAGE_SIZE = 10;

type SortOption = 'newest' | 'oldest';

function getDateGroupKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function getDateGroupLabel(key: string, locale: string): string {
  const [y, m] = key.split('-');
  const month = new Date(parseInt(y, 10), parseInt(m, 10) - 1).toLocaleString(locale === 'da' ? 'da-DK' : 'en-US', { month: 'long' });
  return `${month} ${y}`;
}

function formatTimestamp(iso: string, locale: string): string {
  return new Date(iso).toLocaleString(locale === 'da' ? 'da-DK' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const BlogListPage: React.FC = () => {
  usePageMeta('Blog', 'Blog posts about Danish integration, open source, and side projects.');
  const { t, locale, pathFor } = useLocale();
  const allPosts = useSelector((state: RootState) => state.blog.data);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const sortParam = (searchParams.get('sort') as SortOption) || 'newest';
  const tagParam = searchParams.get('tag') || '';
  const yearParam = searchParams.get('year') || '';

  const [page, setPage] = useState(Math.max(1, pageParam));
  const [sort, setSort] = useState<SortOption>(sortParam);
  const [selectedTag, setSelectedTag] = useState(tagParam);
  const [selectedYear, setSelectedYear] = useState(yearParam);
  const topRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPage(Math.max(1, parseInt(searchParams.get('page') || '1', 10)));
    setSort((searchParams.get('sort') as SortOption) || 'newest');
    setSelectedTag(searchParams.get('tag') || '');
    setSelectedYear(searchParams.get('year') || '');
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
    topRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    const rafId = requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      topRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    (allPosts || []).forEach((p) => p.tags.forEach((t) => set.add(t)));
    return Array.from(set).filter((t) => t !== 'placeholder').sort();
  }, [allPosts]);

  const availableYears = useMemo(() => {
    const set = new Set<number>();
    (allPosts || []).forEach((p) => set.add(new Date(p.date).getFullYear()));
    return Array.from(set).sort((a, b) => b - a);
  }, [allPosts]);

  const filteredAndSorted = useMemo(() => {
    let list = [...(allPosts || [])];
    if (selectedTag) {
      list = list.filter((p) => p.tags.includes(selectedTag));
    }
    if (selectedYear) {
      const y = parseInt(selectedYear, 10);
      list = list.filter((p) => new Date(p.date).getFullYear() === y);
    }
    if (sort === 'newest') {
      list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } else {
      list.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }
    return list;
  }, [allPosts, selectedTag, selectedYear, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAndSorted.slice(start, start + PAGE_SIZE);
  }, [filteredAndSorted, currentPage]);

  const byDateGroup = useMemo(() => {
    const map = new Map<string, BlogPost[]>();
    paginated.forEach((post) => {
      const key = getDateGroupKey(post.date);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(post);
    });
    const entries = Array.from(map.entries());
    return sort === 'newest'
      ? entries.sort(([a], [b]) => b.localeCompare(a))
      : entries.sort(([a], [b]) => a.localeCompare(b));
  }, [paginated, sort]);

  const handleSort = (v: SortOption) => {
    setSort(v);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    next.set('sort', v);
    next.set('page', '1');
    setSearchParams(next);
  };

  const handleTag = (tag: string) => {
    const nextTag = selectedTag === tag ? '' : tag;
    setSelectedTag(nextTag);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (nextTag) next.set('tag', nextTag);
    else next.delete('tag');
    next.set('page', '1');
    setSearchParams(next);
  };

  const handleYear = (year: string) => {
    const nextYear = selectedYear === year ? '' : year;
    setSelectedYear(nextYear);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (nextYear) next.set('year', nextYear);
    else next.delete('year');
    next.set('page', '1');
    setSearchParams(next);
  };

  const goToPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
    setPage(p);
  };

  return (
    <main className="blog-list-page" role="main">
      <div ref={topRef} style={{ position: 'absolute', top: 0, left: 0 }} />
      <div className="blog-list-top-bar">
        <Link to={pathFor('')} className="blog-list-back-link">
          <span className="blog-list-back-arrow">←</span>
          <span>{t('common.backToHome')}</span>
        </Link>
        <LanguageSwitcher />
      </div>

      <div className="blog-list-layout">
        <aside className="blog-list-sidebar">
          <div className="blog-list-sidebar-block">
            <span className="blog-list-sidebar-label">{t('blogList.sort')}</span>
            <div className="blog-list-sort-radios" role="radiogroup" aria-label="Sort posts">
              <label className="blog-list-sort-radio-label">
                <input
                  type="radio"
                  name="sort"
                  value="newest"
                  checked={sort === 'newest'}
                  onChange={() => handleSort('newest')}
                  className="blog-list-sort-radio-input"
                />
                <span>{t('blogList.newestFirst')}</span>
              </label>
              <label className="blog-list-sort-radio-label">
                <input
                  type="radio"
                  name="sort"
                  value="oldest"
                  checked={sort === 'oldest'}
                  onChange={() => handleSort('oldest')}
                  className="blog-list-sort-radio-input"
                />
                <span>{t('blogList.oldestFirst')}</span>
              </label>
            </div>
          </div>

          {availableYears.length > 0 && (
            <div className="blog-list-sidebar-block">
              <span className="blog-list-sidebar-label">{t('blogList.year')}</span>
              <div className="blog-list-sidebar-chips">
                <button
                  type="button"
                  className={`blog-list-sidebar-chip ${!selectedYear ? 'active' : ''}`}
                  onClick={() => handleYear('')}
                >
                  {t('blogList.all')}
                </button>
                {availableYears.map((year) => (
                  <button
                    key={year}
                    type="button"
                    className={`blog-list-sidebar-chip ${selectedYear === String(year) ? 'active' : ''}`}
                    onClick={() => handleYear(String(year))}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}

          {allTags.length > 0 && (
            <div className="blog-list-sidebar-block">
              <span className="blog-list-sidebar-label">{t('blogList.tags')}</span>
              <div className="blog-list-sidebar-chips">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`blog-list-sidebar-chip ${selectedTag === tag ? 'active' : ''}`}
                    onClick={() => handleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        <div className="blog-list-main">
          <header className="blog-list-header">
            <h1 className="blog-list-title">{t('blogList.title')}</h1>
          </header>

          {filteredAndSorted.length === 0 ? (
            <p className="blog-list-empty">{t('blogList.noPosts')}</p>
          ) : (
            <>
              {byDateGroup.map(([dateKey, posts]) => (
                <section key={dateKey} className="blog-list-date-group">
                  <h2 className="blog-list-date-heading">{getDateGroupLabel(dateKey, locale)}</h2>
                  <ul className="blog-list-posts">
                    {posts.map((post) => {
                      const itemContent = (
                        <>
                          <div className="blog-list-item-emoji" aria-hidden>
                            {post.icon === 'terraform' ? (
                              <TerraformIcon size={24} />
                            ) : (
                              post.emoji || '📄'
                            )}
                          </div>
                          <div className="blog-list-item-body">
                            <time className="blog-list-item-time" dateTime={post.date}>
                              {formatTimestamp(post.date, locale)}
                            </time>
                            <h3 className="blog-list-item-title">{post.title}</h3>
                            <p className="blog-list-item-excerpt">{post.excerpt}</p>
                            <BlogTags tags={post.tags} className="blog-list-item-tags" />
                          </div>
                        </>
                      );
                      return (
                        <li key={post.id} className="blog-list-item">
                          {post.externalUrl ? (
                            <a
                              href={post.externalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="blog-list-item-link"
                            >
                              {itemContent}
                            </a>
                          ) : (
                            <Link to={pathFor(`blog/${post.id}`)} className="blog-list-item-link">
                              {itemContent}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}

              {totalPages > 1 && (
                <nav className="blog-list-pagination" aria-label="Pagination">
                  <button
                    type="button"
                    className="blog-list-pagination-btn"
                    disabled={currentPage === 1}
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    {t('blogList.previous')}
                  </button>
                  <span className="blog-list-pagination-info">
                    {t('blogList.pageOf', { current: currentPage, total: totalPages })}
                  </span>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`blog-list-pagination-btn ${p === currentPage ? 'active' : ''}`}
                      onClick={() => goToPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="blog-list-pagination-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    {t('blogList.next')}
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default BlogListPage;
